// Selection logic shared by the File Manager list view, tile view and the
// toolbar's select-all checkbox.
//
// The `selection` array owned by VFSBrowser is the only source of truth (it
// survives a list<->tile toggle). Chonky's own selection store is sealed off
// via <FileBrowser disableSelection>, so this hook is store-free and safe to
// call from outside the <FileBrowser> subtree -- every consumer derives from
// the same `selection` prop.

import { useMemo } from 'react';
import type { FileNode } from '../types/FileManagerTypes';
import { FileAction } from '../enums/fileActions';

export type HeaderCheckboxState = 'unchecked' | 'checked' | 'indeterminate';

interface UseFileListSelectionArgs {
  files: (FileNode | null | undefined)[];
  selection: string[];
  setSelection: (ids: string[]) => void;
  onFileAction: (data: any) => void;
}

export function useFileListSelection({ files, selection, setSelection, onFileAction }: UseFileListSelectionArgs) {
  // Chonky's files prop is Nullable<FileData>[] (used for loading
  // placeholders); filter once here so nothing below has to null-check.
  const visibleFiles: FileNode[] = useMemo(() => files.filter((f): f is FileNode => f != null), [files]);

  const selectedIds = useMemo(() => new Set(selection), [selection]);

  const isSelected = (id: string) => selectedIds.has(id);

  const selectedCount = visibleFiles.filter((f) => isSelected(f.id)).length;

  // Push every selection change back through onFileAction so VFSBrowser's
  // existing handleFileSelection / chonkySelectedFiles pipeline stays the one
  // source it already listens on.
  const dispatch = (ids: string[]) => {
    const idSet = new Set(ids);
    const selectedFiles = visibleFiles.filter((f) => idSet.has(f.id));
    onFileAction({
      id: FileAction.CHANGE_SELECTION,
      state: { selectedFiles, selectedFilesForAction: selectedFiles },
    });
  };

  const toggleSelection = (file: FileNode) => {
    const next = isSelected(file.id) ? selection.filter((id) => id !== file.id) : [...selection, file.id];
    setSelection(next);
    dispatch(next);
  };

  const selectAll = () => {
    const allIds = visibleFiles.map((f) => f.id);
    setSelection(allIds);
    dispatch(allIds);
  };

  const deselectAll = () => {
    setSelection([]);
    dispatch([]);
  };

  const getHeaderCheckboxState = (): HeaderCheckboxState => {
    if (selectedCount === 0) return 'unchecked';
    if (selectedCount === visibleFiles.length && visibleFiles.length > 0) return 'checked';
    return 'indeterminate';
  };

  const toggleSelectAll = () => {
    if (getHeaderCheckboxState() === 'checked') deselectAll();
    else selectAll();
  };

  return {
    visibleFiles,
    isSelected,
    selectedCount,
    toggleSelection,
    selectAll,
    deselectAll,
    getHeaderCheckboxState,
    toggleSelectAll,
  };
}
